import { SettingsMenuItem } from "~/components/settings/Settings";
import { FeatureFlagsConfig, isFeatureFlagEnabled } from "~/featureFlags";

import { MenuGroup, MenuItem } from "./Menu";

type FeatureSubItem = { feature?: string; link?: string };
type FeatureItem = {
  feature?: string;
  subMenu?: FeatureSubItem[];
  link?: string;
};

// allow if feature is not defined on MenuItem
// deny if there is subMenu but all items are filtered out
// if there is exactly 1 subMenu item after filtering -> flatten it into parent
function filterItems(
  items: FeatureItem[],
  config: FeatureFlagsConfig | undefined | null,
): FeatureItem[] {
  return items
    .map((item) => {
      if (item.feature && !isFeatureFlagEnabled(config, item.feature)) {
        return null;
      }

      if (item.subMenu) {
        const filteredSubMenu = item.subMenu.filter((sub) => {
          if (!sub.feature) return true;

          return isFeatureFlagEnabled(config, sub.feature);
        });

        // no sub-items -> remove parent
        if (filteredSubMenu.length === 0) return null;

        // exactly 1 sub-item -> make parent non-collapsable and navigate to that link
        if (filteredSubMenu.length === 1) {
          const only = filteredSubMenu[0];

          return {
            ...item,
            // remove subMenu so Menu logic would not consider collapsable
            subMenu: undefined,
            // parent click leads to the only sub
            link: only.link ?? item.link,
          };
        }

        // 2+ sub-items -> keep collapsable
        return { ...item, subMenu: filteredSubMenu };
      }

      return item;
    })
    .filter(Boolean) as FeatureItem[];
}

// used for the sidebar
export function filterMenuItemsByFeatureFlags(
  menu: MenuItem[] | SettingsMenuItem[],
  config: FeatureFlagsConfig | undefined | null,
): MenuItem[] {
  return filterItems(menu as FeatureItem[], config) as MenuItem[];
}

// used for site-sidebar
export function filterMenuGroupsByFeatureFlags(
  groups: MenuGroup[],
  config: FeatureFlagsConfig | undefined | null,
): MenuGroup[] {
  return groups
    .map((group) => {
      const filtered = filterItems(group.menuItems as FeatureItem[], config);

      if (filtered.length === 0) return null;

      return { ...group, menuItems: filtered as MenuItem[] };
    })
    .filter(Boolean) as MenuGroup[];
}
